import {
  DefaultFetchError,
  FetchCreate,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import { Middah } from "./middot";

export type VaadStatus = "not_started" | "active";
export type VaadDay = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
export type VaadType = "men" | "women";

export interface VaadUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface LearnedMiddah {
  id: string;
  middah: Pick<Middah, "id" | "name">;
}

export interface Vaad {
  id: string;
  name: string;
  vaadId: Record<string, unknown>;
  status: VaadStatus;
  country?: string;
  city: Record<string, unknown>;
  state: Record<string, unknown>;
  address: Record<string, unknown>;
  defaultTime: Record<string, unknown>;
  defaultDay: VaadDay;
  timezone?: string;
  type: VaadType;
  user: VaadUser;
  createdAt: string;
  updatedAt: string;
  middah?: Middah;
  learnedMiddahs?: LearnedMiddah[];
  zoomLink?: string;
}

export interface VaadTableRow extends Vaad {
  key: string;
}

export interface VaadTableData {
  data: VaadTableRow[];
  total: number;
}

export interface VaadCreateParams {
  name: string;
  vaadId: Record<string, unknown>;
  status: VaadStatus;
  country: string;
  city: Record<string, unknown>;
  state: Record<string, unknown>;
  address: Record<string, unknown>;
  defaultTime: Record<string, unknown>;
  defaultDay: VaadDay;
  timezone?: string;
  type: VaadType;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface VaadUpdateParams {
  name?: string;
  vaadId?: Record<string, unknown>;
  status?: VaadStatus;
  country?: string;
  city?: Record<string, unknown>;
  state?: Record<string, unknown>;
  address?: Record<string, unknown>;
  defaultTime?: Record<string, unknown>;
  defaultDay?: VaadDay;
  timezone?: string;
  type?: VaadType;
}

export interface VaadGetParams
  extends
    PagingParams,
    Partial<Pick<Vaad, "name" | "status" | "city" | "state" | "type">> {
  orderByColumn?: keyof Pick<
    Vaad,
    "name" | "status" | "defaultDay" | "type" | "createdAt" | "updatedAt"
  >;
}

export const useVaadGet = <DD = PagingDataResponse<Vaad>>(
  decorateData?: (data: PagingDataResponse<Vaad>) => DD,
): FetchGet<PagingDataResponse<Vaad>, VaadGetParams, DefaultFetchError, DD> =>
  useFetchGet("vaads", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useVaadTableGet = () =>
  useVaadGet<VaadTableData>(
    ({ data, meta }: PagingDataResponse<Vaad>): VaadTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useVaadGetById = <DD = Vaad>(
  id?: string,
  decorateData?: (data: Vaad) => DD,
): FetchGetId<Vaad, DefaultFetchError, unknown, DD> =>
  useFetchGetId("vaads", id, {
    autoStart: !!id,
    decorateData,
  });

export const useVaadCreate = (): FetchCreate<
  VaadCreateParams,
  DefaultFetchError,
  VaadCreateParams
> => useFetchCreate("vaads");

export const useVaadUpdate = (
  id?: string,
): FetchUpdate<VaadUpdateParams, DefaultFetchError, VaadUpdateParams> =>
  useFetchUpdate("vaads", id);

export const useVaadStart = (): FetchCreate<
  FetchSuccess,
  DefaultFetchError,
  void
> =>
  useFetchCreate("vaads", {
    autoStart: false,
    startStateLoading: false,
  });

export interface VaadSendFormResult {
  sent: number;
  total: number;
}

export const useVaadSendForm = (): FetchCreate<
  VaadSendFormResult,
  DefaultFetchError,
  void
> =>
  useFetchCreate("vaads", {
    autoStart: false,
    startStateLoading: false,
  });

export interface MeetingImportResult {
  created?: MeetingImportCreated[];
  vaadNotFound?: MeetingImportFailed[];
  validationFailed?: MeetingImportFailed[];
  totalRows: number;
}

export interface MeetingImportCreated {
  row: number;
  vaadId: string;
  meetingId: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface MeetingImportFailed {
  row: number;
  vaadId: string;
  reason?: string;
  message?: string;
  errors?: string[] | string;
  name?: string;
}

export const useMeetingImport = (): FetchCreate<
  MeetingImportResult,
  DefaultFetchError,
  FormData
> =>
  useFetchCreate("vaads/meetings/import", {
    config: {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    authorization: true,
  });
